import React, { useEffect, useState } from "react";
import { Clock, ArrowLeft } from "lucide-react";
import { RootState, useAppDispatch } from "../../../redux/store";
import { GoogleMap, LoadScript, Marker, Polyline, Polygon } from "@react-google-maps/api";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getEmployeeSites } from "../../../redux/features/common";
import { useSelector } from "react-redux";
import { TrackingSessionService } from "../../../services/firebase/trackingSessionService";
import { LocationPoint } from "../../../types/model/trackingSessionTypes";

const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

const formatPointTime = (point: LocationPoint): string => {
    if (point.recorded_at_local) return point.recorded_at_local;
    if (point.recorded_at) return point.recorded_at;
    if (point.timestamp) return new Date(point.timestamp).toLocaleString();
    return "No timestamp";
};

const ViewTrackingMapPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { tracking_uuid: urlTrackingUuid, emp_account_id: urlEmpAccountId } = useParams(); // Get from URL params
    const [geoLogs, setGeoLogs] = useState<LocationPoint[]>([]);
    const [loading, setLoading] = useState(false);
    const { sites } = useSelector((state: RootState) => state?.common);
    const { user } = useSelector((state: RootState) => state?.user);
    const trackingData = location?.state?.tracking_data;
    
    // Priority: 1. URL param, 2. State, 3. Static fallback
    const staticTrackingUuid = "8788073f-2ad1-4fcc-8e38-0cc20aa92859";
    const trackingUuid = urlTrackingUuid || location.state?.tracking_uuid || staticTrackingUuid;
    
    // Employee account ID priority: 1. URL param, 2. Tracking data, 3. Current user
    const empAccountId = urlEmpAccountId || trackingData?.emp_account_id || user?.os_emp_account_id || "";
    
    // Log which tracking UUID source is being used
    useEffect(() => {
        if (urlTrackingUuid) {
            console.log("✅ Using tracking UUID from URL:", urlTrackingUuid);
        } else if (location.state?.tracking_uuid) {
            console.log("📱 Using tracking UUID from navigation state:", location.state.tracking_uuid);
        } else {
            console.log("🔄 Using static tracking UUID for testing:", staticTrackingUuid);
        }
        console.log("🎯 Final tracking UUID:", trackingUuid);
        
        // Log employee account ID source
        if (urlEmpAccountId) {
            console.log("👤 Using employee account ID from URL:", urlEmpAccountId);
        } else if (trackingData?.emp_account_id) {
            console.log("👤 Using employee account ID from tracking data:", trackingData.emp_account_id);
        } else if (user?.os_emp_account_id) {
            console.log("👤 Using employee account ID from current user:", user.os_emp_account_id);
        } else {
            console.log("⚠️ No employee account ID found");
        }
        console.log("🎯 Final employee account ID:", empAccountId);
    }, [urlTrackingUuid, location.state?.tracking_uuid, staticTrackingUuid, trackingUuid, urlEmpAccountId, trackingData?.emp_account_id, user?.os_emp_account_id, empAccountId]);

    const apiKey = process.env.GOOGLE_MAPS_API_KEY ?? process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";

    // Full screen container style
    const containerStyle = {
        width: "100vw",
        height: "100vh",
        position: "absolute" as const,
        top: 0,
        left: 0,
        zIndex: 1,
    };

    // Calculate bounds for all points (tracking points and geofences)
    const bounds = React.useMemo(() => {
        if (typeof window === "undefined" || !window.google?.maps?.LatLngBounds) {
            return null;
        }

        const newBounds = new window.google.maps.LatLngBounds();

        // Add tracking points to bounds
        geoLogs.forEach((log) => {
            if (isFiniteNumber(log.latitude) && isFiniteNumber(log.longitude)) {
                newBounds.extend({ lat: log.latitude, lng: log.longitude });
            }
        });

        // Add geofence points to bounds
        sites?.forEach((site) => {
            site.geo_fence?.forEach((fence) => {
                fence.paths?.forEach((point) => {
                    if (point.lat && point.lng) {
                        newBounds.extend({ lat: point.lat, lng: point.lng });
                    }
                });
            });
        });

        return newBounds;
    }, [geoLogs, sites]);

    // Handler for map load
    const onMapLoad = React.useCallback(
        (map: google.maps.Map) => {
            console.log("Map loaded successfully");

            if (bounds && (geoLogs.length > 0 || (sites && sites.length > 0)) && !bounds.isEmpty()) {
                console.log("Fitting bounds for", geoLogs.length, "geo logs and", sites?.length || 0, "sites");
                map.fitBounds(bounds);
                // Add a small padding
                setTimeout(() => {
                    const zoom = map.getZoom();
                    if (zoom !== undefined) {
                        map.setZoom(Math.max(zoom - 0.5, 12)); // Ensure minimum zoom level
                    }
                }, 100);
            } else {
                console.log("No bounds to fit, centering map");
                map.setZoom(15);
            }
        },
        [bounds, geoLogs.length, sites]
    );

    const onLoadScriptError = React.useCallback((error: Error) => {
        console.error("LoadScript error:", error);
    }, []);

    const center = React.useMemo(() => {
        if (geoLogs.length > 0) {
            const firstLog = geoLogs[0];
            return {
                lat: firstLog.latitude,
                lng: firstLog.longitude,
            };
        } else {
            // Default center (e.g., New York City)
            return { lat: 40.7128, lng: -74.006 };
        }
    }, [geoLogs]);

    // Create path for polyline from geo logs
    const pathCoordinates = geoLogs.map((log) => ({
        lat: log.latitude,
        lng: log.longitude,
    }));

    // Polyline options for tracking path
    const polylineOptions = {
        strokeColor: "#2563eb",
        strokeOpacity: 0.8,
        strokeWeight: 4,
    };

    // Polygon options for geofences
    const polygonOptions = {
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#FF0000",
        fillOpacity: 0.35,
    };

    // Fetch sites if sites state is empty and we have employee ID from URL
    useEffect(() => {
        if (sites.length === 0 && urlEmpAccountId) {
            console.log("Sites state is empty, fetching sites for employee:", urlEmpAccountId);
            dispatch(getEmployeeSites(urlEmpAccountId));
        }
    }, [dispatch, sites.length, urlEmpAccountId]);

    // Fetch geo logs using the service
    useEffect(() => {
        const fetchGeoLogs = async () => {
            console.log("Fetching geo logs for tracking UUID:", trackingUuid);
            console.log("Tracking Data:", trackingData);
            console.log("User:", user);
            setLoading(true);
            try {
                const logs = await TrackingSessionService.listLocationPoints(trackingUuid);
                const validLogs = logs.filter((point) => isFiniteNumber(point.latitude) && isFiniteNumber(point.longitude));
                console.log("Fetched location_tracking points:", validLogs);
                setGeoLogs(validLogs);
            } catch (error) {
                console.error("Error fetching geo logs:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchGeoLogs();
    }, [trackingUuid, trackingData, user]);

    // Custom marker icon for numbered markers
    const createNumberedMarkerIcon = (number: number) => {
        // Only create icon if Google Maps API is loaded
        if (typeof window !== "undefined" && window.google?.maps?.SymbolPath) {
            return {
                path: window.google.maps.SymbolPath.CIRCLE,
                fillColor: "#2563eb",
                fillOpacity: 0.9,
                strokeColor: "#ffffff",
                strokeWeight: 2,
                scale: 12,
                labelOrigin: new window.google.maps.Point(0, 0),
            };
        }
        // Fallback to default marker if API not loaded
        return undefined;
    };

    // Create map options safely when Google Maps API is available
    const createMapOptions = () => {
        if (typeof window !== "undefined" && window.google?.maps?.MapTypeId) {
            return {
                maxZoom: 20,
                minZoom: 3,
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: true,
                fullscreenControl: true,
                mapTypeId: window.google.maps.MapTypeId.SATELLITE, // Satellite view
                styles: [
                    {
                        featureType: "poi",
                        elementType: "labels",
                        stylers: [{ visibility: "off" }]
                    }
                ]
            };
        }
        // Fallback options without satellite view
        return {
            maxZoom: 20,
            minZoom: 3,
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: true,
            fullscreenControl: true,
            styles: [
                {
                    featureType: "poi",
                    elementType: "labels",
                    stylers: [{ visibility: "off" }]
                }
            ]
        };
    };

    return (
        <div className="relative w-full h-screen bg-black">
            {/* Header overlay */}
            <div className="absolute top-0 left-0 right-0 z-10 bg-white/90 backdrop-blur-sm p-4 border-b">
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-full bg-white/80 hover:bg-white transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-700" />
                    </button>
                    
                    <div className="flex-1 text-center">
                        <div className="text-lg font-semibold text-gray-800">
                            {trackingData?.emp_detail?.emp_name || "Employee Tracking"}
                        </div>
                        {/* <div className="text-xs text-gray-500 mt-1">
                            {urlTrackingUuid ? "📎 From URL" : location.state?.tracking_uuid ? "📱 From Navigation" : "🔄 Test Mode"}
                            {urlEmpAccountId && " | 👤 Employee from URL"}
                        </div> */}
                        {/* Show date from first geo log if available */}
                        {geoLogs.length > 0 && (
                            <div className="text-sm text-gray-600 flex items-center justify-center gap-2">
                                <Clock className="w-4 h-4" />
                                {formatPointTime(geoLogs[0])}
                            </div>
                        )}
                    </div>
                    
                    <div className="w-10"></div> {/* Spacer for centering */}
                </div>
            </div>

            {/* Loading overlay */}
            {loading && (
                <div className="absolute top-20 left-4 z-10 bg-white/90 rounded-lg px-3 py-2">
                    <div className="text-sm text-gray-700">Loading tracking points...</div>
                </div>
            )}

            {/* Sites loading indicator */}
            {sites.length === 0 && urlEmpAccountId && (
                <div className="absolute top-20 left-4 z-10 bg-blue-50 rounded-lg px-3 py-2">
                    <div className="text-sm text-blue-700">Loading sites for employee...</div>
                </div>
            )}

            {/* Tracking points counter */}
            {geoLogs.length > 0 && (
                <div className="absolute top-20 right-4 z-10 bg-white/90 rounded-lg px-3 py-2">
                    <div className="text-sm font-medium text-gray-700">
                        Points: {geoLogs.length}
                    </div>
                </div>
            )}

            {/* Sites counter */}
            {sites.length > 0 && (
                <div className="absolute top-20 right-4 z-10 bg-green-50 rounded-lg px-3 py-2" style={{ top: geoLogs.length > 0 ? '120px' : '80px' }}>
                    <div className="text-sm font-medium text-green-700">
                        Sites: {sites.length}
                    </div>
                </div>
            )}

            {/* Full screen map */}
            {!apiKey ? (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                    <div className="text-center p-4">
                        <p className="text-red-600 font-medium">Google Maps API Key Missing</p>
                        <p className="text-sm text-gray-600 mt-2">
                            Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment
                        </p>
                    </div>
                </div>
            ) : (
                <LoadScript
                    googleMapsApiKey={apiKey}
                    onError={onLoadScriptError}
                    loadingElement={<div>Loading Google Maps...</div>}
                >
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={center}
                        zoom={15}
                        options={createMapOptions()}
                        onLoad={onMapLoad}
                    >
                        {/* Render site geofences */}
                        {sites?.map((site) => {
                            console.log(`Rendering geofences for site: ${site.us_site_code}, fences:`, site.geo_fence?.length || 0);
                            return site.geo_fence?.map((fence, fenceIndex) => {
                                const paths =
                                    fence.paths?.map((point) => ({
                                        lat: point.lat || 0,
                                        lng: point.lng || 0,
                                    })) || [];

                                return (
                                    <Polygon
                                        key={`${site.us_site_code}-fence-${fenceIndex}`}
                                        paths={paths}
                                        options={{
                                            ...polygonOptions,
                                            fillColor: fence.fillColor || "#FF0000",
                                        }}
                                    />
                                );
                            });
                        })}

                        {/* Draw polyline connecting all points */}
                        {pathCoordinates.length > 1 && (
                            <Polyline path={pathCoordinates} options={polylineOptions} />
                        )}

                        {/* Render numbered tracking points */}
                        {geoLogs.map((log, index) => {
                            const markerNumber = index + 1;
                            const markerIcon = createNumberedMarkerIcon(markerNumber);
                            
                            return (
                                <Marker
                                    key={`point-${log.id}-${index}`}
                                    position={{ lat: log.latitude, lng: log.longitude }}
                                    title={`Point ${markerNumber} - ${formatPointTime(log)}`}
                                    label={{
                                        text: markerNumber.toString(),
                                        color: "#ffffff",
                                        fontSize: "12px",
                                        fontWeight: "bold"
                                    }}
                                    icon={markerIcon}
                                />
                            );
                        })}

                        {/* Show "No tracking data" message when no geo logs */}
                        {geoLogs.length === 0 && !loading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
                                <div className="bg-white/90 rounded-lg px-6 py-4 text-center">
                                    <div className="text-lg font-medium text-gray-800 mb-2">No Tracking Data</div>
                                    <div className="text-sm text-gray-600">
                                        No location points found for this tracking session
                                    </div>
                                </div>
                            </div>
                        )}
                    </GoogleMap>
                </LoadScript>
            )}
        </div>
    );
};

export default ViewTrackingMapPage;
